import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { DuelList } from '../components/DuelList'
import {
  IconLangstrecke,
  IconMassband,
  IconPokal,
  IconSprint,
  IconSprung,
} from '../components/Icons'
import { APP_CLAIM, APP_NAME } from '../config'
import { useDevice } from '../DeviceContext'
import { accuracyHint } from '../engine/dpi'
import { loadRecords } from '../engine/records'
import { formatDistanceInline, formatTime } from '../engine/units'

/**
 * Jede Disziplin hat eine eigene Akzentfarbe. Sie zieht sich vom Symbol auf der
 * Startseite ueber die Bahn bis in die Bestenliste durch - damit man an der
 * Farbe erkennt, wo man ist, noch bevor man die Ueberschrift gelesen hat.
 */
const DISCIPLINES = [
  {
    to: '/langstrecke',
    key: 'langstrecke',
    title: 'Langstrecke',
    text: 'Scrollen ohne Ziel. Wie weit kommt dein Daumen?',
    Icon: IconLangstrecke,
    recordKey: 'langstrecke',
    kind: 'distance' as const,
  },
  {
    to: '/sprung',
    key: 'sprung',
    title: 'Sprung',
    text: 'Ein Wisch, ein Ergebnis. Das Nachrollen zählt mit.',
    Icon: IconSprung,
    recordKey: 'sprung',
    kind: 'distance' as const,
  },
  {
    to: '/sprint',
    key: 'sprint',
    title: 'Sprint',
    text: '100 m bis 1 km gegen die Uhr.',
    Icon: IconSprint,
    recordKey: 'sprint:100m',
    kind: 'time' as const,
  },
]

export function HomeScreen() {
  const { metrics } = useDevice()
  const records = useMemo(loadRecords, [])
  const hint = accuracyHint(metrics.confidence)

  return (
    <div className="screen screen--page">
      <div className="page page--home">
        <header className="brand">
          <h1 className="brand__title">{APP_NAME}</h1>
          <p className="brand__claim">{APP_CLAIM}</p>
        </header>

        <nav className="disciplines">
          {DISCIPLINES.map(({ to, key, title, text, Icon, recordKey, kind }) => {
            const best = records.best[recordKey]
            return (
              <Link key={to} to={to} className="disc" data-discipline={key}>
                <span className="disc__glyph">
                  <Icon size={26} />
                </span>
                <span className="disc__body">
                  <span className="disc__title">{title}</span>
                  <span className="disc__text">{text}</span>
                </span>
                <span className="disc__best">
                  {best ? (
                    <>
                      <span className="disc__value">
                        {kind === 'time'
                          ? `${formatTime(best.timeMs)} s`
                          : formatDistanceInline(best.distanceCm)}
                      </span>
                      <span className="disc__label">{kind === 'time' ? '100 m' : 'Bestwert'}</span>
                    </>
                  ) : (
                    <span className="disc__label">Neu</span>
                  )}
                </span>
              </Link>
            )
          })}
        </nav>

        <section className="summary" aria-label="Deine Bilanz">
          <div className="summary__item">
            <span className="summary__value">{formatDistanceInline(records.lifetimeCm)}</span>
            <span className="summary__label">Lebensleistung</span>
          </div>
          <div className="summary__item">
            <span className="summary__value">{records.attempts}</span>
            <span className="summary__label">
              {records.attempts === 1 ? 'Versuch' : 'Versuche'}
            </span>
          </div>
        </section>

        <DuelList />

        <div className="quicklinks">
          <Link to="/bestenliste" className="quicklink">
            <IconPokal size={19} />
            <span>Bestenliste</span>
          </Link>
          <Link to="/kalibrieren" className="quicklink">
            <IconMassband size={19} />
            <span>Kalibrieren</span>
          </Link>
        </div>

        {hint && (
          <p className="notice">
            {hint} <Link to="/kalibrieren">Jetzt nachmessen</Link>
          </p>
        )}

        <p className="meta">
          Maßstab {Math.round(metrics.ppi)} ppi · {metrics.resolutionKey}
        </p>
      </div>
    </div>
  )
}
